/**
 * Admin module manifest — single source of truth for navigation + routing.
 *
 * Each entry drives:
 *   - The sidebar (grouped by section).
 *   - The React Router route table in App.tsx.
 *
 * A module is either:
 *   - PORTED: render the real component lazily.
 *   - STUBBED: render <ModuleStub/> which links back to the legacy
 *     KGAI admin URL on beta.knowledgegate.ai.
 *
 * As Phase D continues and modules get real ports, flip `ported` to
 * true and wire the `element` factory to the actual page component.
 *
 * The manifest is long on purpose: it makes every admin URL
 * accounted-for in one file. Future contributors can see at a glance
 * what's ported vs stubbed.
 */

import { lazy, type ComponentType, type SVGProps } from "react";
import {
  AlertCircle,
  BarChart3,
  Bell,
  BookOpen,
  Boxes,
  BriefcaseBusiness,
  CalendarClock,
  ChartPie,
  ChartSpline,
  CircuitBoard,
  Coins,
  Database,
  Flag,
  FolderOpen,
  FolderSearch,
  HardHat,
  Headset,
  HeartHandshake,
  Home,
  LayoutDashboard,
  LifeBuoy,
  ListTodo,
  MessageCircle,
  MessageSquare,
  MessageSquareWarning,
  Network,
  NotebookPen,
  Package,
  Percent,
  ReceiptText,
  ScrollText,
  Settings as SettingsIcon,
  ShieldCheck,
  ShoppingCart,
  Tag,
  Trophy,
  Users,
  UsersRound,
  Wrench,
  Zap,
} from "lucide-react";
import { ModuleStub } from "@/components/general/ModuleStub";

// ============================================================================
// Lazy page imports for PORTED modules
// ============================================================================

const FeatureFlagsPage = lazy(
  () => import("@/pages/feature-flags/FeatureFlagsPage"),
);
const FeatureFlagDetailPage = lazy(
  () => import("@/pages/feature-flags/FeatureFlagDetailPage"),
);
const CacheMonitoringPage = lazy(
  () => import("@/pages/cache-monitoring/CacheMonitoringPage"),
);
const FeedbackPage = lazy(() => import("@/pages/feedback/FeedbackPage"));

// ============================================================================
// Shape
// ============================================================================

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

export interface AdminModule {
  /** Primary route path shown in the sidebar. Use `:id` etc. for params. */
  path: string;
  /** Display label for sidebar + breadcrumbs. */
  label: string;
  /** Sidebar section header. */
  section: string;
  /** Sidebar icon. */
  Icon: IconComponent;
  /** `true` → render `element`. `false` → render `<ModuleStub/>`. */
  ported: boolean;
  /** React element when ported. Ignored when `ported: false`. */
  element?: React.ReactElement;
  /** Additional routes that render their own element (e.g. detail pages
   *  that don't show up in the sidebar). */
  extraRoutes?: Array<{ path: string; element: React.ReactElement }>;
  /** One-line description used in sidebar tooltip + stub page body. */
  description?: string;
  /** Approximate port size note used on the stub page. */
  sizeNote?: string;
}

// ============================================================================
// The manifest (ordered by section, matching KGAI's sidebar)
// ============================================================================

export const adminManifest: AdminModule[] = [
  // ==========================================================================
  // Overview / landing
  // ==========================================================================
  {
    path: "/",
    label: "Home",
    section: "Overview",
    Icon: Home,
    ported: true,
    description: "Admin portal dashboard.",
  },

  // ==========================================================================
  // Analytics
  // ==========================================================================
  {
    path: "/admin/usage-dashboard",
    label: "Engagement Dashboard",
    section: "Analytics",
    Icon: ChartPie,
    ported: false,
    description: "Realtime engagement metrics, DAU/WAU, study time.",
    sizeNote: "2,617 lines across 16 files.",
  },
  {
    path: "/admin/sales-dashboard",
    label: "Sales Dashboard",
    section: "Analytics",
    Icon: ChartSpline,
    ported: false,
    description: "Revenue trends, conversion funnels, cohort analysis.",
    sizeNote: "60 lines but pulls from a large analytics components tree.",
  },
  {
    path: "/admin/company-expenses",
    label: "Company Expenses",
    section: "Analytics",
    Icon: ReceiptText,
    ported: false,
    description: "Expense tracking, categorization, reporting.",
    sizeNote: "4,215 lines across 23 files.",
  },
  {
    path: "/admin/educator-payments",
    label: "Educator Payments",
    section: "Analytics",
    Icon: Coins,
    ported: false,
    description: "Educator-facing payment records and payouts.",
    sizeNote: "2,946 lines.",
  },
  {
    path: "/admin/activity-tracking",
    label: "Admin Activity Tracking",
    section: "Analytics",
    Icon: ListTodo,
    ported: false,
    description: "Audit log of admin actions across the portal.",
    sizeNote: "2,156 lines across 12 files.",
  },

  // ==========================================================================
  // Product Management
  // ==========================================================================
  {
    path: "/admin/products/topology",
    label: "Topology Management",
    section: "Product Management",
    Icon: Network,
    ported: false,
    description: "Course/subject hierarchy, topics, subtopics.",
    sizeNote: "Largest admin module (~60K lines across 164 files) — includes course/bundle listings, content builder, and more.",
  },
  {
    path: "/admin/products/course-listing",
    label: "Courses & Bundles",
    section: "Product Management",
    Icon: BookOpen,
    ported: false,
    description: "Course catalog, bundles, pricing, visibility.",
    sizeNote: "Part of the Products module (~60K lines).",
  },
  {
    path: "/admin/products/add-ons",
    label: "Add Ons",
    section: "Product Management",
    Icon: Package,
    ported: false,
    description: "Admin-managed add-on products (exam mentors, interviews, etc.).",
  },
  {
    path: "/admin/products/assets",
    label: "Asset Library",
    section: "Product Management",
    Icon: FolderOpen,
    ported: false,
    description: "Video, PDF, and image assets shared across courses.",
  },
  {
    path: "/admin/question-bank",
    label: "Question Bank",
    section: "Product Management",
    Icon: FolderSearch,
    ported: false,
    description: "Master question repository with tags, topologies, metadata.",
    sizeNote: "18,496 lines across 49 files — second-largest admin module.",
  },
  {
    path: "/admin/tests",
    label: "Tests",
    section: "Product Management",
    Icon: NotebookPen,
    ported: false,
    description: "Test creation, scheduling, attempt analytics.",
    sizeNote: "4,121 lines across 22 files.",
  },
  {
    path: "/admin/live-classes",
    label: "Live Classes",
    section: "Product Management",
    Icon: CalendarClock,
    ported: false,
    description: "Live class scheduling + analytics.",
    sizeNote: "2,939 lines across 12 files.",
  },

  // ==========================================================================
  // Admissions & Certificates
  // ==========================================================================
  {
    path: "/admin/products/paid-admissions",
    label: "Admissions",
    section: "Admissions & Certificates",
    Icon: ShoppingCart,
    ported: false,
    description: "Paid admissions by course/bundle.",
  },
  {
    path: "/admin/test-attempts",
    label: "Test Attempts",
    section: "Admissions & Certificates",
    Icon: ListTodo,
    ported: false,
    description: "Student test-attempt records and scoring.",
    sizeNote: "1,588 lines across 6 files.",
  },
  {
    path: "/admin/certificates",
    label: "Certificates",
    section: "Admissions & Certificates",
    Icon: Trophy,
    ported: false,
    description: "Certificate issuing + verification.",
    sizeNote: "2,266 lines across 13 files.",
  },
  {
    path: "/admin/admissions/invoices",
    label: "Invoices & Credit Notes",
    section: "Admissions & Certificates",
    Icon: ReceiptText,
    ported: false,
    description: "Admission invoices and credit note issuance.",
  },
  {
    path: "/admin/admissions/transactions",
    label: "Transactions",
    section: "Admissions & Certificates",
    Icon: Coins,
    ported: false,
    description: "Payment transaction ledger.",
  },

  // ==========================================================================
  // Student Management
  // ==========================================================================
  {
    path: "/admin/students",
    label: "Students",
    section: "Student Management",
    Icon: Users,
    ported: false,
    description: "Student profiles, enrollment, access.",
    sizeNote: "Uses cross-cutting UserList component (~1,000 lines total deps).",
  },

  // ==========================================================================
  // Marketing
  // ==========================================================================
  {
    path: "/admin/notifications",
    label: "Notifications",
    section: "Marketing",
    Icon: Bell,
    ported: false,
    description: "Push/email notification campaigns.",
    sizeNote: "1,991 lines across 8 files.",
  },
  {
    path: "/admin/referrals",
    label: "Refer and Earn",
    section: "Marketing",
    Icon: HeartHandshake,
    ported: false,
    description: "Referral program management.",
    sizeNote: "2,359 lines across 12 files.",
  },

  // ==========================================================================
  // Lead Management
  // ==========================================================================
  {
    path: "/admin/lead-management/counselor",
    label: "Counselor View",
    section: "Lead Management",
    Icon: Headset,
    ported: false,
    description: "Counselor-facing lead list, calls, notes, status updates.",
    sizeNote: "Part of lead-management (~27K lines across 82 files).",
  },
  {
    path: "/admin/lead-management/manager",
    label: "Manager View",
    section: "Lead Management",
    Icon: BarChart3,
    ported: false,
    description: "Manager-level lead pipeline + counselor performance.",
    sizeNote: "Part of lead-management (~27K lines across 82 files).",
  },

  // ==========================================================================
  // AI & Communities
  // ==========================================================================
  {
    path: "/admin/ask-ai-mentor",
    label: "AI Mentor",
    section: "AI & Communities",
    Icon: Wrench,
    ported: false,
    description: "Admin view of AI Mentor conversations + settings.",
  },
  {
    path: "/admin/course-captain-chats",
    label: "Course Captain Chats",
    section: "AI & Communities",
    Icon: MessageCircle,
    ported: false,
    description: "Chats between students and course captains.",
    sizeNote: "702 lines.",
  },
  {
    path: "/admin/comments",
    label: "Community Posts",
    section: "AI & Communities",
    Icon: MessageSquare,
    ported: false,
    description: "Comment moderation on community posts.",
    sizeNote: "1,707 lines.",
  },
  {
    path: "/admin/communities-settings/blocked-users",
    label: "Blocked Users",
    section: "AI & Communities",
    Icon: ShieldCheck,
    ported: false,
    description: "Community-blocked user management.",
  },

  // ==========================================================================
  // Placements
  // ==========================================================================
  {
    path: "/admin/placements/job-updates",
    label: "Job Updates",
    section: "Placements",
    Icon: BriefcaseBusiness,
    ported: false,
    description: "Placement job postings and updates.",
    sizeNote: "Part of placements (~3K lines across 24 files).",
  },
  {
    path: "/admin/placements/resume-review",
    label: "Resume Review",
    section: "Placements",
    Icon: ScrollText,
    ported: false,
    description: "Student resume review queue.",
  },
  {
    path: "/admin/placements/personal-mentor",
    label: "Personal Mentor",
    section: "Placements",
    Icon: UsersRound,
    ported: false,
    description: "Personal mentor assignment + sessions.",
  },
  {
    path: "/admin/placements/mock-interview",
    label: "Mock Interview",
    section: "Placements",
    Icon: Headset,
    ported: false,
    description: "Mock interview scheduling + feedback.",
  },

  // ==========================================================================
  // Predictors
  // ==========================================================================
  {
    path: "/admin/gate-rank-predictor/requests",
    label: "GATE Rank — Requests",
    section: "Predictors",
    Icon: AlertCircle,
    ported: false,
    description: "GATE rank predictor user requests.",
    sizeNote: "Part of gate-rank-predictor (~2,758 lines across 13 files).",
  },
  {
    path: "/admin/gate-rank-predictor/question-paper",
    label: "GATE Rank — Question Paper",
    section: "Predictors",
    Icon: NotebookPen,
    ported: false,
  },
  {
    path: "/admin/gate-rank-predictor/leaderboard",
    label: "GATE Rank — Leaderboard",
    section: "Predictors",
    Icon: Trophy,
    ported: false,
  },
  {
    path: "/admin/gate-rank-predictor/marketing",
    label: "GATE Rank — Marketing",
    section: "Predictors",
    Icon: Tag,
    ported: false,
  },
  {
    path: "/admin/net-result-predictor/attempts",
    label: "NET Result — Attempts",
    section: "Predictors",
    Icon: ListTodo,
    ported: false,
    description: "NET result predictor attempt records.",
    sizeNote: "Part of net-result-predictor (~2,215 lines across 16 files).",
  },
  {
    path: "/admin/net-result-predictor/answer-keys",
    label: "NET Result — Answer Keys",
    section: "Predictors",
    Icon: NotebookPen,
    ported: false,
  },
  {
    path: "/admin/net-result-predictor/leaderboard",
    label: "NET Result — Leaderboard",
    section: "Predictors",
    Icon: Trophy,
    ported: false,
  },
  {
    path: "/admin/net-result-predictor/export-users",
    label: "NET Result — Export Users",
    section: "Predictors",
    Icon: UsersRound,
    ported: false,
  },

  // ==========================================================================
  // HR & Payroll
  // ==========================================================================
  {
    path: "/admin/hr/jobs",
    label: "HR — Jobs",
    section: "HR & Payroll",
    Icon: HardHat,
    ported: false,
    description: "Internal HR job postings + applications.",
    sizeNote: "4,291 lines across 10 files.",
  },
  {
    path: "/admin/payroll",
    label: "Payroll",
    section: "HR & Payroll",
    Icon: Percent,
    ported: false,
    description: "Employee payroll runs.",
    sizeNote: "1,398 lines.",
  },

  // ==========================================================================
  // Platform (infra / ops)
  // ==========================================================================
  {
    path: "/admin/feature-flags",
    label: "Feature Flags",
    section: "Platform",
    Icon: Flag,
    ported: true,
    element: <FeatureFlagsPage />,
    extraRoutes: [
      { path: "/admin/feature-flags/:id", element: <FeatureFlagDetailPage /> },
    ],
    description: "Feature flag admin: kill switches, rollout %, A/B.",
  },
  {
    path: "/admin/version-management",
    label: "Version Management",
    section: "Platform",
    Icon: Boxes,
    ported: false,
    description: "App version deploy tracking.",
    sizeNote: "426 lines.",
  },
  {
    path: "/admin/cache-monitoring",
    label: "Cache Monitoring",
    section: "Platform",
    Icon: Database,
    ported: true,
    element: <CacheMonitoringPage />,
    description: "Redis cache stats + clear operations.",
  },
  {
    path: "/admin/communities-settings",
    label: "Communities Settings",
    section: "Platform",
    Icon: SettingsIcon,
    ported: false,
    description: "Communities module configuration.",
    sizeNote: "2,135 lines.",
  },
  {
    path: "/admin/ai-settings",
    label: "AI Settings",
    section: "Platform",
    Icon: CircuitBoard,
    ported: false,
    description: "AI features configuration.",
    sizeNote: "4,454 lines across 5 files.",
  },
  {
    path: "/admin/seo",
    label: "SEO",
    section: "Platform",
    Icon: Zap,
    ported: false,
    description: "SEO metadata management.",
    sizeNote: "835 lines.",
  },

  // ==========================================================================
  // Support
  // ==========================================================================
  {
    path: "/admin/feedback",
    label: "Feedback",
    section: "Support",
    Icon: MessageSquareWarning,
    ported: true,
    element: <FeedbackPage />,
    description: "User feedback timeline + status management.",
  },
  {
    path: "/admin/question-reports",
    label: "Question Reports",
    section: "Support",
    Icon: AlertCircle,
    ported: false,
    description: "Student-submitted question reports queue.",
    sizeNote: "2,066 lines.",
  },
  {
    path: "/admin/counselor-reports",
    label: "Counselor Reports",
    section: "Support",
    Icon: LifeBuoy,
    ported: false,
    description: "Counselor-submitted incident/support reports.",
    sizeNote: "528 lines.",
  },
  {
    path: "/admin/communities-reports",
    label: "Community Reports",
    section: "Support",
    Icon: LifeBuoy,
    ported: false,
    description: "Community-reported content moderation queue.",
    sizeNote: "584 lines.",
  },
  {
    path: "/admin/security",
    label: "Security",
    section: "Support",
    Icon: ShieldCheck,
    ported: false,
    description: "Security events + admin audit trails.",
    sizeNote: "2,215 lines.",
  },

  // ==========================================================================
  // Org
  // ==========================================================================
  {
    path: "/admin/settings/profile",
    label: "Settings",
    section: "Org",
    Icon: SettingsIcon,
    ported: false,
    description: "Current-admin settings and profile.",
  },
  {
    path: "/admin/org-chart",
    label: "Org Chart",
    section: "Org",
    Icon: UsersRound,
    ported: false,
    description: "Org hierarchy + reporting lines.",
    sizeNote: "6,819 lines across 15 files.",
  },
  {
    path: "/admin/task-tracker",
    label: "Task Tracker",
    section: "Org",
    Icon: LayoutDashboard,
    ported: false,
    description: "Internal task tracker (Linear-style).",
    sizeNote: "16,045 lines across 36 files — fourth-largest admin module.",
  },
];

// ============================================================================
// Helpers
// ============================================================================

/** Group manifest by section (preserving section-order). */
export function groupedBySection(): Array<{
  section: string;
  items: AdminModule[];
}> {
  const seen = new Set<string>();
  const order: string[] = [];
  for (const m of adminManifest) {
    if (!seen.has(m.section)) {
      seen.add(m.section);
      order.push(m.section);
    }
  }
  return order.map((section) => ({
    section,
    items: adminManifest.filter((m) => m.section === section),
  }));
}

/** Build the element for a module — real component or stub. */
export function buildElement(m: AdminModule): React.ReactElement {
  if (m.ported && m.element) return m.element;

  const legacyPath = m.path.replace(/\/:[^/]+/g, "");

  return (
    <ModuleStub
      name={m.label}
      description={m.description}
      legacyPath={legacyPath}
      sizeNote={m.sizeNote}
    />
  );
}
